import React from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Wordmark } from "@/components/BrandMark";
import { LogOut } from "lucide-react";
import { NAV_LINKS as links } from "@/lib/navLinks";

export default function Sidebar() {
  const { user, access, logout } = useAuth();
  const navigate = useNavigate();
  const isPro = !!access?.has_access;
  return (
    <aside className="w-64 shrink-0 hidden md:flex flex-col border-r border-[var(--border)] bg-[var(--surface)] p-4 min-h-screen">
      <div className="mb-8 px-2" data-testid="sidebar-brand">
        <Link to="/app/home" title="Dynasty Metrics home" className="inline-block">
          <Wordmark size={32} sub="DYNASTY ANALYTICS" />
        </Link>
      </div>

      <nav className="flex flex-col gap-1 flex-1">
        {links.map(l => (
          <NavLink key={l.to} to={l.to} end={l.end} data-testid={l.tid}
            className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
            <l.icon className="w-4 h-4" />
            <span className="text-sm flex-1">{l.label}</span>
            {l.pro && !isPro && (
              <span className="text-[8px] font-mono tracking-widest text-[var(--accent)] border border-[var(--accent)]/40 rounded-sm px-1 py-0.5" data-testid={`pro-badge-${l.tid}`}>PRO</span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="mt-4 border-t border-[var(--border)] pt-4 space-y-2">
        {access && !access.is_subscribed && (
          <div className="card-bordered p-3" data-testid="trial-card">
            <div className={`text-[10px] tracking-widest ${access.is_beta ? "text-[var(--accent)]" : "text-[var(--text-3)]"}`}>
              {access.is_beta ? "BETA ACCESS" : access.on_trial ? "TRIAL" : "FREE PLAN"}
            </div>
            {(access.is_beta || access.on_trial) ? (
              <div className="font-mono text-xl">{access.days_left}d</div>
            ) : (
              <div className="text-xs text-[var(--text-2)] mt-0.5">Calculator + Rankings free</div>
            )}
            <button onClick={() => navigate("/pricing")} className={`text-xs w-full mt-2 ${access.is_beta ? "btn-secondary" : "btn-primary"}`} data-testid="upgrade-btn">
              {access.is_beta ? "Subscribe (optional)" : "Upgrade — $9.99/mo"}
            </button>
          </div>
        )}
        {access?.is_subscribed && (
          <div className="card-bordered p-3" data-testid="sub-card">
            <div className="text-[10px] tracking-widest text-emerald-400">
              {access.cancel_at_period_end ? "CANCELS SOON" : "PRO · ACTIVE"}
            </div>
            <div className="font-mono text-xl">{access.days_left}d</div>
            <button onClick={() => navigate("/pricing")} className="btn-secondary text-xs w-full mt-2" data-testid="manage-sub-btn">
              Manage billing
            </button>
          </div>
        )}
        <div className="flex items-center gap-2 px-2 py-2">
          <div className="w-7 h-7 rounded-full bg-[var(--elev)] border border-[var(--border)] flex items-center justify-center text-xs font-mono">
            {(user?.name || "?")[0]?.toUpperCase()}
          </div>
          <div className="flex-1 overflow-hidden">
            <div className="text-sm truncate" data-testid="sidebar-user">{user?.name}</div>
            <div className="text-[10px] text-[var(--text-3)] truncate">{user?.email}</div>
          </div>
          <button onClick={logout} className="text-[var(--text-3)] hover:text-white" data-testid="logout-btn" title="Logout">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}
